import * as React from "react"
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Checkbox, CircularProgress, IconButton, TextField, } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { AXIOS_API_INSTANCE, SHORT_DATE_FORMAT } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from "dayjs"
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Chart from 'react-apexcharts'
import { findUser, useUsers } from "../ReactQuery/QueryUsers";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import UserAvatar from "../UserAvatar";
import { useTrackedDarkMode } from "../UserState"
import { formatDuration, moneyFormat, stringAvatar } from "../../Utils"
import InfoModal from "../modals/InfoModal";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />
const help = <React.Fragment>
    <span><b>Wrench Hours:</b> Total time worked on service kits</span><br />
    <span><b>Diagnostic:</b> Total time recorded against "Diagnostic"</span><br />
    <span><b>Overhead:</b> Total time not recorded against a service kit or diagnostic</span><br />
    <span><b>Service Complete:</b> Total time recorded against a completed and invoiced service</span><br />
    <span><b>Service Incomplete:</b> Total time recorded against a service that is not completed and invoiced</span><br />
    <span><b>Customer Invoiced:</b> Total time invoiced against a customer independent of time worked</span><br />
    <span><b>Efficiency:</b> Service Complete Time compared to Time Invoiced. <i>Formula:</i> <math><mrow><mfrac><mrow><mi>Customer Invoice hrs</mi></mrow><mrow><mi>Service Complete hrs</mi></mrow></mfrac><mo>-</mo><mn>1</mn></mrow></math></span><br />
    <span><b>Billing Rate:</b> The average rate per hour worked invoiced to the customer</span><br />
</React.Fragment>


export default function ServiceTechnicianWorkHistory({ startDatetime, endDatetime, selectedLocations }) {
    const { enqueueSnackbar } = useSnackbar();
    const [darkMode,] = useTrackedDarkMode();
    const { data: users, isLoadingUsers } = useUsers();
    const [selectedUsers, setSelectedUsers] = React.useState([])
    const [newSelectedUsers, setNewSelectedUsers] = React.useState([])
    const [selectedUsersOpen, setSelectedUsersOpen] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(false)
    const [, setServiceOrderWorkHistory] = React.useState([])
    const [isLoadingEfficiency, setIsLoadingEfficiency] = React.useState(false)
    const [serviceOrderServicesEfficiency, setServiceOrderServicesEfficiency] = React.useState({})
    const [series, setSeries] = React.useState([])
    const [userData, setUserData] = React.useState({})
    const [tableData, setTableData] = React.useState([])
    const [infoModal, setInfoModal] = React.useState({ open: false, title: '', content: '' })
    const navigate = useNavigate();
    const borderColor = darkMode ? '#FFF' : 'rgba(0, 0, 0, 0.87)'

    const handleUserChange = (e, options, action, option) => {
        if (action === "removeOption") {
            if (!selectedUsersOpen) {
                setSelectedUsers(structuredClone(selectedUsers.filter((item) => item.id !== option.option.id)))
            }
            setNewSelectedUsers(structuredClone(newSelectedUsers.filter((item) => item.id !== option.option.id)))
        } else {
            newSelectedUsers.push(option.option)
            setNewSelectedUsers(structuredClone(newSelectedUsers))
        }
    }

    const build_data = (serviceOrderWorkHistory, serviceOrderServicesEfficiency) => {
        const my_series = selectedUsers.map((x) => { return { 'name': x.email, 'data': [], color: stringAvatar(x.name || '').sx.bgcolor } })
        serviceOrderWorkHistory.forEach((x) => {
            const goals = []
            const minutes_reported = x.service_order_work_history_entries.reduce((a, b) => a + b.recorded_minutes, 0) + (x.diagnostic_minutes ?? 0)
            var end_time = dayjs.utc(x.end_time).valueOf()
            if (dayjs.utc(x.start_time).add(minutes_reported, 'm').valueOf() > end_time) {
                end_time = dayjs.utc(x.start_time).add(minutes_reported, 'm').valueOf()
                goals.push({
                    name: 'Time Worked',
                    value: dayjs.utc(x.end_time).valueOf(),
                    strokeColor: '#CD2F2A'
                })
            }
            if (selectedUsers.find((y) => y.id === x.user_id)) {
                my_series[selectedUsers.findIndex((y) => y.id === x.user_id)]['data'].push({
                    x: 'Time',
                    y: [dayjs.utc(x.start_time).valueOf(), end_time],
                    goals: goals
                })
            }
        })

        const user_data = {}
        selectedUsers.forEach((user) => user_data[user.id] = { minutes: 0, diagnostic: 0, overhead: 0, service_complete: 0, service_incomplete: 0, customer_invoiced: 0, billed_price: 0 })

        const table_data = []
        serviceOrderWorkHistory.forEach((x) => {
            if (!user_data[x.user_id]) {
                return
            }
            var end_time = dayjs.utc(x.end_time)
            // Minutes on service records + diagnostic time
            const minutes_reported = x.service_order_work_history_entries.reduce((a, b) => a + b.recorded_minutes, 0) + (x.diagnostic_minutes ?? 0)
            // overhead: anything between start and end time not recorded against a service or diagnostics
            const overhead = Math.abs(end_time.diff(dayjs.utc(x.start_time), 'minutes')) - minutes_reported
            user_data[x.user_id].overhead += Math.max(0, overhead)
            user_data[x.user_id].diagnostic += x.diagnostic_minutes ?? 0
            x.service_order_work_history_entries.forEach((entry) => {
                table_data.push({
                    id: entry.id,
                    start_time: x.start_time,
                    end_time: x.end_time,
                    recorded_minutes: entry.recorded_minutes,
                    user_id: x.user_id,
                    location_id: x.location_id,
                    note: entry.note,
                    service_order_id: x.service_order_id,
                    service_order_service: entry.service_order_service
                })
                // total wrench minutes 
                user_data[x.user_id].minutes += entry.recorded_minutes
                if (entry.service_order_service_id && serviceOrderServicesEfficiency[entry.service_order_service_id.toString()]) {
                    // service complete minutes
                    user_data[x.user_id].service_complete += entry.recorded_minutes
                    // recorded minutes times this service's time efficiency gives us the amount of time invoiced by this technician
                    user_data[x.user_id].customer_invoiced += entry.recorded_minutes * serviceOrderServicesEfficiency[entry.service_order_service_id.toString()].time_worked_efficiency
                    user_data[x.user_id].billed_price += entry.recorded_minutes * serviceOrderServicesEfficiency[entry.service_order_service_id.toString()].minute_price
                } else {
                    user_data[x.user_id].service_incomplete += entry.recorded_minutes
                }
            })
            if (x.diagnostic_minutes) {
                table_data.push({
                    id: crypto.randomUUID(),
                    start_time: x.start_time,
                    end_time: x.end_time,
                    recorded_minutes: x.diagnostic_minutes,
                    user_id: x.user_id,
                    location_id: x.location_id,
                    note: 'DIAGNOSTIC' + (x.diagnostic_note ? ` - ${x.diagnostic_note}` : ''),
                    service_order_id: x.service_order_id,
                    service_order_service: { name: "DIAGNOSTIC" },
                })
            }
            if (overhead > 1) {
                table_data.push({
                    id: crypto.randomUUID(),
                    start_time: x.start_time,
                    end_time: x.end_time,
                    recorded_minutes: overhead,
                    user_id: x.user_id,
                    location_id: x.location_id,
                    note: 'OVERHEAD' + (x.overhead_note ? ` - ${x.overhead_note}` : ''),
                    service_order_id: x.service_order_id,
                    service_order_service: undefined,
                })
            }
        })

        Object.keys(user_data).forEach((u) => {
            const efficiency = (user_data[u].customer_invoiced / user_data[u].service_complete) - 1
            if (isFinite(efficiency)) {
                if (efficiency > 0) {
                    user_data[u].efficiency_element = (<span style={{ color: 'green' }}>+{(efficiency * 100).toFixed(1)}%</span>)
                } else {
                    user_data[u].efficiency_element = (<span style={{ color: 'red' }}>{(efficiency * 100).toFixed(1)}%</span>)
                }
            } else {
                user_data[u].efficiency_element = (<span style={{ color: 'red' }}>-%</span>)
            }
            user_data[u].efficiency = isFinite(efficiency) ? efficiency : 0
        })
        setSeries(my_series)
        setTableData(table_data)
        setUserData(user_data)
    }

    const getWorkHistory = (start_datetime, end_datetime, assignee_user_ids, selectedLocations, reload_efficiency) => {
        if (assignee_user_ids.length < 1) {
            return
        }
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("serviceorderworkhistory", {
                params: {
                    filters: JSON.stringify({
                        'and': [
                            { 'user_id.in': assignee_user_ids },
                            { 'end_time.gte': start_datetime },
                            { 'end_time.lte': end_datetime },
                            { 'location_id.in': selectedLocations.map((x) => x.id) },
                        ]
                    }),
                    order_by: 'date_created',
                    order_by_direction: 'asc',
                },
            }).then((workHistory) => {
                if (!reload_efficiency) {
                    build_data(workHistory.data.data, serviceOrderServicesEfficiency)
                    setServiceOrderWorkHistory(workHistory.data.data)
                    setIsLoading(false)
                } else {
                    send_authorized_request(() =>
                        AXIOS_API_INSTANCE.get("reports/serviceorder_service_efficiency", {
                            params: {
                                start_datetime: start_datetime,
                                end_datetime: end_datetime.add(1, 'day'),
                                location_ids: selectedLocations.map((x) => x.id),
                            },
                        }).then((report) => {
                            Object.keys(report.data.data).forEach((x) => {
                                const service = report.data.data[x]
                                const time_worked_efficiency = service.official_minutes_worked / service.total_time
                                const minute_price = (
                                    (service.official_flat_rate +
                                        (service.official_hourly_rate * service.official_minutes_worked / (60)))
                                    / service.total_time)
                                report.data.data[x].minute_price = isFinite(minute_price) ? minute_price : 0
                                report.data.data[x].time_worked_efficiency = isFinite(time_worked_efficiency) ? time_worked_efficiency : 0
                            })
                            setServiceOrderServicesEfficiency(report.data.data)
                            build_data(workHistory.data.data, report.data.data)
                            setServiceOrderWorkHistory(workHistory.data.data)
                            setIsLoadingEfficiency(false)
                            setIsLoading(false)
                        }).catch((error) => {
                            setIsLoadingEfficiency(false)
                            setIsLoading(false)
                            enqueueSnackbar(`Failed to load data <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                        })
                    )
                }
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to load data <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    const sort_technicians = (a, b) => {
        if (a.permissions_service_tech && !b.permissions_service_tech) {
            return -1
        } else if (!a.permissions_service_tech && b.permissions_service_tech) {
            return 1
        } else {
            return a.name.localeCompare(b.name)
        }
    }

    React.useEffect(() => {
        getWorkHistory(startDatetime, endDatetime, selectedUsers.map((x) => x.id), selectedLocations, Object.keys(serviceOrderServicesEfficiency).length === 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUsers])

    React.useEffect(() => {
        getWorkHistory(startDatetime, endDatetime, selectedUsers.map((x) => x.id), selectedLocations, true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDatetime, endDatetime, selectedLocations])

    return (
        <React.Fragment>
            <Box>
                <LoadingBackdropIndeterminate
                    open={Boolean(isLoading || isLoadingUsers)}
                    text="Loading Work History Report"
                />
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    loading={isLoadingUsers}
                    loadingText={"Loading Technicians"}
                    clearIcon={<div></div>}
                    sx={{ width: "100%", p: 1, pt: 0 }}
                    onChange={handleUserChange}
                    onOpen={(e) => setSelectedUsersOpen(true)}
                    onClose={(e) => {
                        if (JSON.stringify(newSelectedUsers.sort()) !== JSON.stringify(selectedUsers.sort())) {
                            setSelectedUsers(structuredClone(newSelectedUsers))
                        }
                        setSelectedUsersOpen(false)
                    }}
                    getOptionLabel={(option) => option.name}
                    value={newSelectedUsers}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    options={users.toSorted(sort_technicians)}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                                value={option.id}
                            />
                            {`${option.name || ""}${option.permissions_service_tech ? " (Technician)" : ""}`}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Technicians"
                            placeholder="Technicians"
                        />
                    )}
                />
                {!isLoadingUsers && !isLoading &&
                    <Box>
                        <p>Performance report for technicians.
                            <IconButton onClick={() => {
                                const newData = {
                                    title: "Service Technician Work History",
                                    content: help,
                                    maxWidth: "md",
                                    level: "help",
                                }
                                setInfoModal({
                                    open: true,
                                    ...newData,
                                    callback: () => { setInfoModal({ open: false, ...newData }) },
                                })
                            }}>
                                <HelpIcon />
                            </IconButton>
                        </p>
                        {
                            selectedUsers.length > 0 && Object.keys(userData).length > 0 &&
                            <Grid container columns={{ md: 6, lg: 12, xl: 12 }} spacing={1}>
                                {selectedUsers.map((user) => {
                                    if (!userData[user.id]) {
                                        return undefined
                                    } else {
                                        const userBorderColor = stringAvatar(user?.name || '').sx.bgcolor
                                        return (
                                            <Grid key={user.id} container size={6} columns={6} sx={{ border: `3px solid ${userBorderColor}`, borderRadius: '16px', p: 1 }} alignItems={'center'} justifyContent={'center'}>
                                                <Grid size={6} container>
                                                    <Grid container size={"grow"} space={1} alignItems="center">
                                                        <Grid sx={{ pr: 0 }} size="auto">
                                                            <span><UserAvatar user={user} tooltip={user?.name} /></span>
                                                        </Grid>
                                                        <Grid size="grow">
                                                            <span><b>{user?.name ?? "None"}</b></span>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid size={"auto"}>
                                                        <span><b>Total: {((userData[user.id].minutes + userData[user.id].diagnostic + userData[user.id].overhead) / 60).toFixed(2)}h</b></span>
                                                    </Grid>
                                                </Grid>
                                                <Grid size={2} sx={{ textAlign: 'center' }}>
                                                    <span>Wrench Hours: {(userData[user.id].minutes / 60).toFixed(2)}h</span>
                                                </Grid>
                                                <Grid size={2} sx={{ borderLeft: `1px solid ${borderColor}`, borderRight: `1px solid ${borderColor}`, textAlign: 'center' }}>
                                                    <span>Diagnostic: {(userData[user.id].diagnostic / 60).toFixed(2)}h</span>
                                                </Grid>
                                                <Grid size={2} sx={{ textAlign: 'center' }}>
                                                    <span>Overhead: {(userData[user.id].overhead / 60).toFixed(2)}h</span>
                                                </Grid>
                                                <Grid size={2} sx={{ textAlign: 'center' }}>
                                                    {isLoadingEfficiency ?
                                                        <CircularProgress color="grey" size={"15px"} />
                                                        :
                                                        <span>Service Complete: {(userData[user.id].service_complete / 60).toFixed(2)}h</span>
                                                    }
                                                </Grid>
                                                <Grid size={2} sx={{ borderLeft: `1px solid ${borderColor}`, borderRight: `1px solid ${borderColor}`, textAlign: 'center' }}>
                                                    {isLoadingEfficiency ?
                                                        <CircularProgress color="grey" size={"15px"} />
                                                        :
                                                        <span>Service incomplete: {(userData[user.id].service_incomplete / 60).toFixed(2)}h</span>
                                                    }
                                                </Grid>
                                                <Grid size={2} sx={{ textAlign: 'center' }}>
                                                    {isLoadingEfficiency ?
                                                        <CircularProgress color="grey" size={"15px"} />
                                                        :
                                                        <span>Customer Invoiced: {((isFinite(userData[user.id].customer_invoiced) ? userData[user.id].customer_invoiced : 0) / 60).toFixed(2)}h</span>
                                                    }
                                                </Grid>
                                                <Grid size={2} sx={{ textAlign: 'center' }}>
                                                    {isLoadingEfficiency ?
                                                        <CircularProgress color="grey" size={"15px"} />
                                                        :
                                                        <span>Efficiency: <b>{userData[user.id].efficiency_element}</b></span>
                                                    }
                                                </Grid>
                                                <Grid size={2} sx={{ textAlign: 'center' }}>
                                                    {isLoadingEfficiency ?
                                                        <CircularProgress color="grey" size={"15px"} />
                                                        :
                                                        <span>Billing Rate: ${moneyFormat((userData[user.id].billed_price / userData[user.id].service_complete) * 60)}/hr</span>
                                                    }
                                                </Grid>
                                            </Grid>)
                                    }
                                })}
                            </Grid>
                        }
                        <Accordion slotProps={{ transition: { unmountOnExit: true } }} sx={{ mb: 2, mt: 2 }} disableGutters>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                            >
                                <h3 style={{ margin: 0 }}>Timeline</h3>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 1 }}>
                                <Chart
                                    type="rangeBar" height={450} width={"100%"}
                                    options={{
                                        chart: {
                                            type: 'rangeBar',
                                            animations: {
                                                enabled: false
                                            }
                                        },
                                        plotOptions: {
                                            bar: {
                                                horizontal: true,
                                                barHeight: '80%',
                                                rangeBarOverlap: false
                                            }
                                        },
                                        xaxis: {
                                            type: 'datetime',
                                            // min: startDatetime.valueOf(),
                                            labels: {
                                                datetimeUTC: false,
                                                // format: SHORT_DATE_FORMAT,
                                                // formatter: function (value) {
                                                //     return (users && users.data.data.find((x) => x.id === value)?.name) ?? value;
                                                // }
                                            }
                                        },
                                        yaxis: {
                                            labels: {
                                                // formatter: function (value) {
                                                //     return (users && users.data.data.find((x) => x.id === value)?.name) ?? value;
                                                // }
                                            },
                                        },
                                        stroke: {
                                            width: 1
                                        },
                                        fill: {
                                            type: 'solid',
                                            opacity: 0.6
                                        },
                                        legend: {
                                            position: 'top',
                                            horizontalAlign: 'left'
                                        },
                                        grid: {
                                            show: true,
                                            xaxis: {
                                                lines: {
                                                    show: true
                                                }
                                            },
                                        },
                                        tooltip: {
                                            x: {
                                                formatter: function (value, time, c, d, e) {
                                                    if (Number.isInteger(value)) {
                                                        return dayjs.utc(value).local().format(SHORT_DATE_FORMAT)
                                                    } else {
                                                        return 'Time'
                                                    }
                                                },
                                                // title: {
                                                //     formatter: (seriesName) => { return (users && users.data.data.find((x) => x.id === seriesName)?.name) ?? seriesName },
                                                // },
                                            },
                                        },
                                        noData: {
                                            text: 'No Data',
                                        }
                                    }}
                                    series={series}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <DataGrid
                            rows={tableData}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{ toolbar: { csvOptions: { fileName: `ProfitMaxPro_Technician_Work_History` } } }}
                            columns={[
                                {
                                    field: "start_time",
                                    headerName: "Date",
                                    width: 235,
                                    editable: false,
                                    sortable: true,
                                    renderCell: (params) => {
                                        return <span>{dayjs.utc(params.value).local().format('M/D/YYYY')} ({dayjs.utc(params.value).local().format('h:mmA')} - {dayjs.utc(params.row.end_time).local().format('h:mmA')})</span>
                                    }
                                },
                                {
                                    field: "user_id",
                                    headerName: "Technician",
                                    width: 135,
                                    editable: false,
                                    sortable: true,
                                    filterable: false,
                                    renderCell: (params) => {
                                        const user = findUser(users, params.row.user_id)
                                        return (
                                            (<Grid container space={1} alignItems="center" sx={{ height: '100%' }}>
                                                <Grid sx={{ pr: 1 }} size="auto">
                                                    <span><UserAvatar user={user} tooltip={user?.name} /></span>
                                                </Grid>
                                            </Grid>)
                                        );
                                    }
                                },
                                {
                                    field: "service_order_id",
                                    headerName: "Service",
                                    width: 200,
                                    editable: false,
                                    sortable: true,
                                    renderCell: (params) => {
                                        return (
                                            (<Button onClick={(e) => { e.preventDefault(); navigate(`/ServiceOrders/${params.value}`) }} href={`/ServiceOrders/${params.value}`}>
                                                SO #{params.value} {params.row.service_order_service?.name ?? 'OVERHEAD'}
                                            </Button>)
                                        );
                                    }
                                },
                                {
                                    field: "note",
                                    headerName: "Note",
                                    flex: 1,
                                    editable: false,
                                    sortable: false,
                                },
                                {
                                    field: "recorded_minutes",
                                    headerName: "Time",
                                    width: 75,
                                    editable: false,
                                    sortable: true,
                                    valueFormatter: (value) => {
                                        return formatDuration(value)
                                    }
                                },
                            ]}
                            autoHeight={true}
                            disableRowSelectionOnClick
                            disablep
                        />
                    </Box>
                }
            </Box>
            <InfoModal
                {...infoModal}
            />
        </React.Fragment >
    )
}
